import React, { useState } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, HelpCircle, User } from 'lucide-react';
import { fmtDay, fmtMonth, fmtWeekday, isWeekend, roomIssues, statusRuns, STATUS_LABEL, STATUS_STYLE } from './calUtils';

const COL = 'min-w-[46px] w-[46px]';
const LABEL = 'sticky left-0 z-10 bg-white text-xs text-slate-700 pr-2 pl-2 whitespace-nowrap border-r';

// Two header rows exactly like the extranet: month labels spanning their days, then "Sat / 24" per column.
export const GridHead = ({ days }) => {
  const months = [];
  days.forEach((iso) => {
    const m = iso.slice(0, 7);
    const last = months[months.length - 1];
    if (last && last.m === m) last.span += 1; else months.push({ m, span: 1, iso });
  });
  const monthCells = months.map((x) => <th key={x.m} colSpan={x.span} className="text-left text-xs font-semibold text-slate-700 pb-1 pl-1 border-l">{fmtMonth(x.iso)}</th>);
  const dayCells = days.map((iso) => (
    <th key={iso} className={`${COL} text-center font-normal pb-1 ${isWeekend(iso) ? 'bg-slate-100' : ''} ${fmtDay(iso) === '01' ? 'border-l' : ''}`} data-testid={`rc-head-${iso}`}>
      <span className="block text-[10px] text-slate-500">{fmtWeekday(iso)}</span><span className="block text-xs font-semibold text-slate-900">{fmtDay(iso)}</span>
    </th>
  ));
  return (
    <thead>
      <tr><th className={LABEL} /> {monthCells}</tr>
      <tr><th className={LABEL} /> {dayCells}</tr>
    </thead>
  );
};

const StatusRow = ({ room, days, onPick }) => {
  const runs = statusRuns(days, room);
  const cells = runs.map((r) => (
    <td key={r.from} colSpan={r.span} className="p-0.5">
      <button type="button" onClick={() => onPick(room, r.from, 'status', r.to)} className={`w-full h-6 rounded-sm text-[11px] font-semibold text-left px-2 truncate ${STATUS_STYLE[r.status]}`} data-testid={`rc-status-${room.room_id}-${r.from}`}>{STATUS_LABEL[r.status]}</button>
    </td>
  ));
  return <tr data-testid={`rc-status-row-${room.room_id}`}><td className={LABEL}>Room status</td>{cells}</tr>;
};

const ValueRow = ({ room, days, label, field, read, testKey, onPick, muted, icon }) => {
  const cells = days.map((iso) => {
    const d = room.days[iso];
    const closed = d && d.numAvail === 0;
    const v = read(iso, d);
    const tone = !d ? 'bg-slate-50 text-slate-300' : closed ? 'bg-red-50 text-red-800' : isWeekend(iso) ? 'bg-slate-50 text-slate-900' : 'bg-white text-slate-900';
    const content = <span className={`block text-center text-xs ${muted ? 'text-slate-500' : 'font-semibold'}`}>{v ?? '—'}</span>;
    return (
      <td key={iso} className={`${COL} p-0.5 ${fmtDay(iso) === '01' ? 'border-l' : ''}`}>
        {onPick ? <button type="button" onClick={() => onPick(room, iso, field)} className={`w-full h-7 rounded-sm border hover:ring-2 hover:ring-[#003b95] ${tone}`} data-testid={`rc-${testKey}-${room.room_id}-${iso}`}>{content}</button>
          : <div className={`w-full h-7 rounded-sm border flex items-center justify-center ${tone}`} data-testid={`rc-${testKey}-${room.room_id}-${iso}`}>{content}</div>}
      </td>
    );
  });
  return <tr><td className={LABEL}><span className="inline-flex items-center gap-1">{icon}{label}</span></td>{cells}</tr>;
};

const OccupancyRows = ({ room, days }) => {
  const counts = [];
  for (let n = room.max_people - 1; n >= 1; n -= 1) counts.push(n);
  return counts.map((n) => <ValueRow key={n} room={room} days={days} field="occ" testKey={`occ${n}`} muted icon={<User className="w-3 h-3" />} label={`× ${n}`} read={(iso) => { const p = room.occupancy[iso]?.[String(n)]; return p != null ? `€${Math.round(p)}` : null; }} />);
};

export const RoomBlock = ({ room, days, perGuest, restrictions, onPick }) => {
  const [open, setOpen] = useState(false);
  const expanded = open || perGuest;
  const issues = roomIssues(days, room);
  const span = days.length + 1;
  return (
    <tbody data-testid={`rc-room-${room.room_id}`}>
      <tr><td colSpan={span} className="pt-4 pb-1 px-2 sticky left-0">
        <span className="text-sm font-bold text-slate-900">{room.room}</span> <span className="text-xs text-slate-500">(Room ID: {room.room_id})</span>
        {issues > 0 && <span className="ml-3 inline-flex items-center gap-1 text-xs text-red-700" data-testid={`rc-issues-${room.room_id}`}><AlertCircle className="w-3.5 h-3.5" /> Possible issues ({issues})</span>}
      </td></tr>
      <StatusRow room={room} days={days} onPick={onPick} />
      <ValueRow room={room} days={days} label="Rooms to sell" field="avail" testKey="avail" onPick={onPick} read={(iso, d) => d?.numAvail} />
      <ValueRow room={room} days={days} label="Net booked" field="booked" testKey="booked" muted read={(iso) => room.booked[iso] || 0} />
      <tr><td colSpan={span} className="pt-2 pb-0.5 px-2 sticky left-0">
        <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#003b95] underline" data-testid={`rc-plan-toggle-${room.room_id}`}>{expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Standard Rate</button>
      </td></tr>
      <ValueRow room={room} days={days} label={`× ${room.max_people}`} icon={<User className="w-3 h-3" />} field="price" testKey="price" onPick={onPick} read={(iso, d) => (d?.price1 != null ? `€${Math.round(d.price1)}` : null)} />
      {expanded && <OccupancyRows room={room} days={days} />}
      {restrictions && <ValueRow room={room} days={days} label="Minimum length of stay" icon={<HelpCircle className="w-3 h-3 text-slate-400" />} field="minstay" testKey="minstay" onPick={onPick} read={(iso, d) => d?.minStay ?? 1} />}
    </tbody>
  );
};
